/**
 * What a right-click (or a long press) offers on each thing of the agents screen: an agent, a folder,
 * a group chat, the tab row itself, a past conversation, a routine. Each menu is built when it opens,
 * from the state as it is then.
 */
import { Eye, EyeOff, FolderInput, FolderMinus, FolderPlus, Link2, Mail, MailOpen, MessagesSquare, Pause, Pencil, Pin, PinOff, Play, Plus, Trash2, UserPlus, Users, Zap } from 'lucide-react'
import { Nut } from './icons'
import { Glyph, I, askName, askSure, type Icon, type PopEntry } from './kit'
import { useDemo } from './store'

const st = () => useDemo.getState()
const ic = (icon: Icon) => <I icon={icon} className="text-ws-icon" />
const copy = (hash: string) => {
  void navigator.clipboard?.writeText(`${location.origin}${location.pathname}#/${hash}`).catch(() => undefined)
  st().say('Link copied')
}
const rules = (...parts: PopEntry[][]): PopEntry[] => parts.filter((p) => p.length).flatMap((p, i) => (i ? ['rule' as const, ...p] : p))

/* ── things asked for by name ────────────────────────────────────────── */

export const newFolder = (first?: string) => askName({ title: 'New folder', cta: 'Create folder', done: (name) => st().addFolder(name, first ? [first] : []) })
export const renameAgent = (id: string) => {
  const a = st().agents.find((x) => x.id === id)
  if (a) askName({ title: 'Rename agent', value: a.name, cta: 'Rename', done: (name) => st().patchAgent(id, { name }) })
}
export const deleteAgent = (id: string) => {
  const a = st().agents.find((x) => x.id === id)
  if (a) askSure({ title: `Delete ${a.name}?`, body: 'Its memory, its routines and its conversations are deleted with it.', cta: 'Delete', done: () => st().removeAgent(id) })
}
export const disbandGroup = (id: string) => {
  const g = st().groups.find((x) => x.id === id)
  if (g) askSure({ title: `Disband ${g.name}?`, body: 'The agents stay. The conversations of this group chat are deleted.', cta: 'Disband', done: () => st().removeGroup(id) })
}

/* ── the lists that open beside a row ────────────────────────────────── */

/** Where an agent is kept: one of your folders, a new one, or none. */
export function moveList(id: string): PopEntry[] {
  const s = st()
  const at = s.folders.find((f) => f.members.includes(id))
  return rules(
    s.folders.map((f) => ({ label: f.name, checked: f.id === at?.id, onSelect: () => s.setFolder(id, f.id) })),
    [{ label: 'New folder', icon: ic(FolderPlus), fixed: true, onSelect: () => newFolder(id) }, ...(at ? [{ label: `Take out of ${at.name}`, icon: ic(FolderMinus), fixed: true, onSelect: () => s.setFolder(id, null) }] : [])],
  )
}
/** The group chats an agent takes part in — tick to add, tick again to leave. */
export function chatList(id: string, only?: 'others'): PopEntry[] {
  const s = st()
  return rules(
    s.groups.filter((g) => only !== 'others' || !g.members.includes(id)).map((g) => ({ label: g.name, hint: `${g.members.length}`, checked: g.members.includes(id), keep: only !== 'others', onSelect: () => s.toggleMember(g.id, id) })),
    [{ label: 'New group chat', icon: ic(Plus), fixed: true, onSelect: () => s.set({ groupDialog: `new:${id}` }) }],
  )
}
/** The agents kept in a folder — tick to bring one in. */
export function folderAgents(id: string): PopEntry[] {
  const s = st()
  return s.agents.map((a) => {
    const at = s.folders.find((f) => f.members.includes(a.id))
    return { label: a.name, icon: <Glyph palette={a.palette} />, hint: at && at.id !== id ? at.name : undefined, checked: at?.id === id, keep: true, onSelect: () => st().setFolder(a.id, at?.id === id ? null : id) }
  })
}
/** The agents of a group chat — tick to add or remove. */
export function groupAgents(id: string): PopEntry[] {
  const s = st()
  const g = s.groups.find((x) => x.id === id)
  return s.agents.map((a) => ({ label: a.name, icon: <Glyph palette={a.palette} />, checked: !!g?.members.includes(a.id), keep: true, onSelect: () => st().toggleMember(id, a.id) }))
}

/* ── the menus ───────────────────────────────────────────────────────── */

export function agentMenu(id: string): PopEntry[] {
  const s = st()
  const a = s.agents.find((x) => x.id === id)
  if (!a) return []
  const kept = s.folders.some((f) => f.members.includes(id))
  return rules(
    [
      ...(kept ? [] : [{ label: a.pinned ? 'Unpin' : 'Pin', icon: ic(a.pinned ? PinOff : Pin), onSelect: () => s.patchAgent(id, { pinned: !a.pinned }) }]),
      { label: 'Move to', icon: ic(FolderInput), sub: () => moveList(id) },
      { label: 'Add to group chat', icon: ic(MessagesSquare), sub: () => chatList(id) },
    ],
    [
      { label: s.unread[id] ? 'Mark as read' : 'Mark as unread', icon: ic(s.unread[id] ? MailOpen : Mail), onSelect: () => s.markUnread(id, !s.unread[id]) },
      { label: 'Rename', icon: ic(Pencil), onSelect: () => renameAgent(id) },
      { label: 'Settings', icon: ic(Nut), onSelect: () => (s.openAgent(id), s.set({ panel: 'settings', routineId: null })) },
      { label: 'Copy link', icon: ic(Link2), onSelect: () => copy(`agents/${id}`) },
      a.hidden ? { label: 'Show in the tab row', icon: ic(Eye), onSelect: () => s.patchAgent(id, { hidden: false }) } : { label: 'Hide from the tab row', icon: ic(EyeOff), onSelect: () => (s.patchAgent(id, { hidden: true }), s.say(`${a.name} is hidden — Search still finds it`)) },
    ],
    [{ label: 'Delete', icon: <I icon={Trash2} />, danger: true, onSelect: () => deleteAgent(id) }],
  )
}

export function folderMenu(id: string): PopEntry[] {
  const s = st()
  const f = s.folders.find((x) => x.id === id)
  if (!f) return []
  return rules(
    [
      { label: 'Agents', icon: ic(UserPlus), sub: () => folderAgents(id) },
      { label: 'Rename', icon: ic(Pencil), onSelect: () => askName({ title: 'Rename folder', value: f.name, cta: 'Rename', done: (name) => st().patchFolder(id, { name }) }) },
    ],
    [{ label: 'Disband folder', icon: ic(FolderMinus), onSelect: () => s.removeFolder(id) }],
  )
}

export function groupMenu(id: string): PopEntry[] {
  const s = st()
  const g = s.groups.find((x) => x.id === id)
  if (!g) return []
  return rules(
    [
      { label: 'Members', icon: ic(Users), sub: () => groupAgents(id) },
      { label: s.unread[id] ? 'Mark as read' : 'Mark as unread', icon: ic(s.unread[id] ? MailOpen : Mail), onSelect: () => s.markUnread(id, !s.unread[id]) },
      { label: 'Rename', icon: ic(Pencil), onSelect: () => askName({ title: 'Rename group chat', value: g.name, cta: 'Rename', done: (name) => st().patchGroup(id, { name }) }) },
      { label: 'Copy link', icon: ic(Link2), onSelect: () => copy(`agents/${id}`) },
    ],
    [{ label: 'Disband group chat', icon: <I icon={Trash2} />, danger: true, onSelect: () => disbandGroup(id) }],
  )
}

/** The tab row itself: what can be added to it, and what is hidden from it. */
export function barMenu(): PopEntry[] {
  const s = st()
  const hidden = s.agents.filter((a) => a.hidden)
  return rules(
    [
      { label: 'New agent', icon: ic(Plus), onSelect: () => s.set({ addAgent: true }) },
      { label: 'New folder', icon: ic(FolderPlus), onSelect: () => newFolder() },
      { label: 'New group chat', icon: ic(MessagesSquare), onSelect: () => s.set({ groupDialog: 'new' }) },
    ],
    hidden.length ? [{ label: 'Hidden agents', icon: ic(EyeOff), hint: `${hidden.length}`, sub: () => st().agents.filter((a) => a.hidden).map((a) => ({ label: a.name, icon: <Glyph palette={a.palette} />, onSelect: () => (st().patchAgent(a.id, { hidden: false }), st().openAgent(a.id)) })) }] : [],
  )
}

export function sessionMenu(id: string): PopEntry[] {
  const s = st()
  const x = s.sessions.find((q) => q.id === id) ?? s.temp.find((q) => q.id === id)
  if (!x) return []
  const open = () => (x.agentId ? s.openSession(x.agentId, id) : s.openTemp(id))
  return rules(
    [
      { label: 'Open', icon: ic(MessagesSquare), onSelect: open },
      { label: 'Rename', icon: ic(Pencil), onSelect: () => askName({ title: 'Rename conversation', value: x.title, cta: 'Rename', done: (title) => st().renameSession(id, title) }) },
      { label: 'Copy link', icon: ic(Link2), onSelect: () => copy(x.agentId ? `agents/${x.agentId}/${id}` : `chats/${id}`) },
    ],
    [{ label: 'Delete', icon: <I icon={Trash2} />, danger: true, onSelect: () => askSure({ title: 'Delete this conversation?', body: `“${x.title}” is deleted. What the agent learned from it stays in its memory.`, cta: 'Delete', done: () => st().removeSession(id) }) }],
  )
}

export function routineMenu(id: string): PopEntry[] {
  const s = st()
  const r = s.routines.find((x) => x.id === id)
  if (!r) return []
  return rules(
    [
      { label: 'Open', icon: ic(Pencil), onSelect: () => (s.openAgent(r.agentId), s.set({ panel: 'routines', routineId: id })) },
      { label: r.on ? 'Pause' : 'Resume', icon: ic(r.on ? Pause : Play), onSelect: () => s.toggleRoutine(id) },
      { label: 'Run now', icon: ic(Zap), disabled: r.result === 'running', onSelect: () => s.runRoutine(id) },
    ],
    [{ label: 'Delete', icon: <I icon={Trash2} />, danger: true, onSelect: () => askSure({ title: 'Delete this routine?', body: `“${r.name}” stops running and is removed.`, cta: 'Delete', done: () => st().removeRoutine(id) }) }],
  )
}
